import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  InputAdornment,
  Chip,
  Tooltip,
  Alert,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Avatar,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Store as StoreIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Person as ManagerIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  BusinessCenter as HQIcon,
  LocalShipping as WarehouseIcon,
  ShoppingBag as RetailIcon,
  Inventory as StockIcon,
  Assessment as ReportIcon,
} from "@mui/icons-material";

const StoreMaster = () => {
  // Sample stores data
  const [stores, setStores] = useState([
    {
      id: 1,
      storeCode: "HQ001",
      storeName: "Headquarters - Mumbai",
      storeType: "Headquarters",
      address: "123 Marine Drive, Mumbai, Maharashtra - 400001",
      city: "Mumbai",
      state: "Maharashtra",
      pincode: "400001",
      phone: "+91 22 1234 5678",
      email: "hq.mumbai@company.com",
      managerName: "Rajesh Kumar",
      managerPhone: "+91 98765 43210",
      gstNo: "27AAAAA0000A1Z5",
      totalStock: 15000,
      activeStatus: true,
      openingDate: "2020-01-01",
    },
    {
      id: 2,
      storeCode: "WH001",
      storeName: "Main Warehouse - Navi Mumbai",
      storeType: "Warehouse",
      address: "Plot No. 45, MIDC, Navi Mumbai, Maharashtra - 400701",
      city: "Navi Mumbai",
      state: "Maharashtra",
      pincode: "400701",
      phone: "+91 22 2345 6789",
      email: "warehouse.navimumbai@company.com",
      managerName: "Priya Sharma",
      managerPhone: "+91 98765 43211",
      gstNo: "27BBBBB0000B1Z5",
      totalStock: 50000,
      activeStatus: true,
      openingDate: "2020-03-15",
    },
    {
      id: 3,
      storeCode: "RT001",
      storeName: "Retail Store - Andheri",
      storeType: "Retail",
      address: "456 Andheri West, Mumbai, Maharashtra - 400058",
      city: "Mumbai",
      state: "Maharashtra",
      pincode: "400058",
      phone: "+91 22 3456 7890",
      email: "retail.andheri@company.com",
      managerName: "Amit Patel",
      managerPhone: "+91 98765 43212",
      gstNo: "27CCCCC0000C1Z5",
      totalStock: 8500,
      activeStatus: true,
      openingDate: "2020-06-10",
    },
    {
      id: 4,
      storeCode: "RT002",
      storeName: "Retail Store - Pune",
      storeType: "Retail",
      address: "789 FC Road, Pune, Maharashtra - 411004",
      city: "Pune",
      state: "Maharashtra",
      pincode: "411004",
      phone: "+91 20 1234 5678",
      email: "retail.pune@company.com",
      managerName: "Sneha Desai",
      managerPhone: "+91 98765 43213",
      gstNo: "27DDDDD0000D1Z5",
      totalStock: 7200,
      activeStatus: true,
      openingDate: "2021-01-20",
    },
    {
      id: 5,
      storeCode: "RT003",
      storeName: "Retail Store - Thane",
      storeType: "Retail",
      address: "321 Ghodbunder Road, Thane, Maharashtra - 400607",
      city: "Thane",
      state: "Maharashtra",
      pincode: "400607",
      phone: "+91 22 4567 8901",
      email: "retail.thane@company.com",
      managerName: "Vikram Singh",
      managerPhone: "+91 98765 43214",
      gstNo: "27EEEEE0000E1Z5",
      totalStock: 6800,
      activeStatus: false,
      openingDate: "2021-08-05",
    },
  ]);

  const [searchQuery, setSearchQuery] = useState("");
  const [storeTypeFilter, setStoreTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [successMessage, setSuccessMessage] = useState("");
  const [openAddEditModal, setOpenAddEditModal] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [editingStore, setEditingStore] = useState(null);
  const [viewingStore, setViewingStore] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    storeCode: "",
    storeName: "",
    storeType: "Retail",
    address: "",
    city: "",
    state: "",
    pincode: "",
    phone: "",
    email: "",
    managerName: "",
    managerPhone: "",
    gstNo: "",
    totalStock: 0,
    activeStatus: true,
    openingDate: "",
  });

  // Helper functions
  const getFilteredStores = () => {
    let filtered = stores;

    if (searchQuery) {
      filtered = filtered.filter((store) =>
        Object.values(store).some((value) =>
          String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
      );
    }

    if (storeTypeFilter !== "all") {
      filtered = filtered.filter((store) => store.storeType === storeTypeFilter);
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter((store) => 
        statusFilter === "active" ? store.activeStatus : !store.activeStatus
      );
    }

    return filtered;
  };

  const getStoreTypeIcon = (type) => {
    switch (type) {
      case 'Headquarters': return <HQIcon sx={{ color: '#9c27b0' }} />;
      case 'Warehouse': return <WarehouseIcon sx={{ color: '#ff9800' }} />;
      case 'Retail': return <RetailIcon sx={{ color: '#2196f3' }} />;
      default: return <StoreIcon />;
    }
  };

  const getStoreTypeColor = (type) => {
    switch (type) {
      case 'Headquarters': return { bg: '#f3e5f5', color: '#9c27b0' };
      case 'Warehouse': return { bg: '#fff3e0', color: '#ff9800' };
      case 'Retail': return { bg: '#e3f2fd', color: '#2196f3' };
      default: return { bg: '#f5f5f5', color: '#666' };
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  // Handlers
  const handleOpenAdd = () => {
    setEditingStore(null);
    setFormData({
      storeCode: "",
      storeName: "",
      storeType: "Retail",
      address: "",
      city: "",
      state: "",
      pincode: "",
      phone: "",
      email: "",
      managerName: "",
      managerPhone: "",
      gstNo: "",
      totalStock: 0,
      activeStatus: true,
      openingDate: "",
    });
    setOpenAddEditModal(true);
  };

  const handleOpenEdit = (store) => {
    setEditingStore(store);
    setFormData(store);
    setOpenAddEditModal(true);
  };

  const handleCloseAddEdit = () => {
    setOpenAddEditModal(false);
    setEditingStore(null);
  };

  const handleSubmit = () => {
    if (!formData.storeCode || !formData.storeName || !formData.city) {
      alert("Please fill in all required fields");
      return;
    }

    if (editingStore) {
      setStores(stores.map(store => store.id === editingStore.id ? { ...formData, id: editingStore.id } : store));
      setSuccessMessage(`Store ${formData.storeName} updated successfully!`);
    } else {
      const newStore = { ...formData, id: stores.length + 1 };
      setStores([...stores, newStore]);
      setSuccessMessage(`Store ${formData.storeName} added successfully!`);
    }

    setTimeout(() => setSuccessMessage(""), 3000);
    handleCloseAddEdit();
  };

  const handleView = (store) => {
    setViewingStore(store);
    setOpenViewModal(true);
  };

  const handleToggleStatus = (store) => {
    setStores(stores.map(s => 
      s.id === store.id ? { ...s, activeStatus: !s.activeStatus } : s
    ));
    setSuccessMessage(`Store ${store.storeName} ${!store.activeStatus ? 'activated' : 'deactivated'}!`);
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const handleDelete = (store) => {
    if (window.confirm(`Are you sure you want to delete ${store.storeName}?`)) {
      setStores(stores.filter(s => s.id !== store.id));
      setSuccessMessage(`Store ${store.storeName} deleted successfully!`);
      setTimeout(() => setSuccessMessage(""), 3000);
    }
  };

  const handleChangePage = (_, newPage) => setPage(newPage);
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredStores = getFilteredStores();

  // Calculate statistics
  const totalStores = stores.length;
  const activeStores = stores.filter(s => s.activeStatus).length;
  const totalStockAllStores = stores.reduce((sum, s) => sum + s.totalStock, 0);
  const hqCount = stores.filter(s => s.storeType === "Headquarters").length;
  const warehouseCount = stores.filter(s => s.storeType === "Warehouse").length;
  const retailCount = stores.filter(s => s.storeType === "Retail").length;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <StoreIcon sx={{ color: 'primary.main', fontSize: 40 }} />
          <Box>
            <Typography variant="h4" fontWeight="bold" color="primary">
              Store Master
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage multiple stores and locations • {filteredStores.length} stores
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ReportIcon />}
            sx={{ borderRadius: 2 }}
          >
            View Reports
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAdd}
            sx={{
              borderRadius: 2,
              px: 3,
              fontWeight: 600,
              backgroundColor: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
            }}
          >
            Add New Store
          </Button>
        </Box>
      </Box>

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
          {successMessage}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ borderRadius: 2, boxShadow: 2, borderLeft: '4px solid #1976d2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Total Stores</Typography>
                  <Typography variant="h4" fontWeight="bold" color="primary.main">
                    {totalStores}
                  </Typography>
                </Box>
                <StoreIcon sx={{ fontSize: 48, color: 'primary.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ borderRadius: 2, boxShadow: 2, borderLeft: '4px solid #2e7d32' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Active</Typography>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {activeStores}
                  </Typography>
                </Box>
                <ActiveIcon sx={{ fontSize: 48, color: 'success.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ borderRadius: 2, boxShadow: 2, borderLeft: '4px solid #9c27b0' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">HQ</Typography>
                  <Typography variant="h4" fontWeight="bold" sx={{ color: '#9c27b0' }}>
                    {hqCount}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    WH: {warehouseCount} | RT: {retailCount}
                  </Typography>
                </Box>
                <HQIcon sx={{ fontSize: 48, color: '#9c27b0', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ borderRadius: 2, boxShadow: 2, borderLeft: '4px solid #ff9800' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Total Stock</Typography>
                  <Typography variant="h5" fontWeight="bold" color="warning.main">
                    {totalStockAllStores.toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">units</Typography>
                </Box>
                <StockIcon sx={{ fontSize: 48, color: 'warning.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ borderRadius: 2, boxShadow: 2, borderLeft: '4px solid #d32f2f' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Inactive</Typography>
                  <Typography variant="h4" fontWeight="bold" color="error.main">
                    {totalStores - activeStores}
                  </Typography>
                </Box>
                <InactiveIcon sx={{ fontSize: 48, color: 'error.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Filter Section */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Search Stores..."
              variant="outlined"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Store Type</InputLabel>
              <Select
                value={storeTypeFilter}
                onChange={(e) => setStoreTypeFilter(e.target.value)}
                label="Store Type"
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="Headquarters">Headquarters</MenuItem>
                <MenuItem value="Warehouse">Warehouse</MenuItem>
                <MenuItem value="Retail">Retail</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                setSearchQuery("");
                setStoreTypeFilter("all");
                setStatusFilter("all");
              }}
              startIcon={<RefreshIcon />}
            >
              Reset
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Table Section */}
      <Paper sx={{ borderRadius: 2, boxShadow: 3, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Store Code</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Store Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Location</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Manager</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Stock</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredStores.length > 0 ? (
                filteredStores.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((store) => {
                  const typeStyle = getStoreTypeColor(store.storeType);
                  return (
                    <TableRow key={store.id} sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="700" sx={{ fontFamily: 'monospace' }}>
                          {store.storeCode}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getStoreTypeIcon(store.storeType)}
                          <Box>
                            <Typography variant="body2" fontWeight="600">{store.storeName}</Typography>
                            <Typography variant="caption" color="text.secondary">{store.email}</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={store.storeType}
                          size="small"
                          sx={{
                            backgroundColor: typeStyle.bg,
                            color: typeStyle.color,
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <LocationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2">{store.city}, {store.state}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32, fontSize: '0.875rem' }}>
                            {store.managerName.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight="600">{store.managerName}</Typography>
                            <Typography variant="caption" color="text.secondary">{store.managerPhone}</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="700" color="primary">
                          {store.totalStock.toLocaleString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">units</Typography>
                      </TableCell>
                      <TableCell>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={store.activeStatus}
                              onChange={() => handleToggleStatus(store)}
                              color="success"
                            />
                          }
                          label={
                            <Chip
                              label={store.activeStatus ? "Active" : "Inactive"}
                              size="small"
                              color={store.activeStatus ? "success" : "error"}
                              sx={{ fontWeight: 600 }}
                            />
                          }
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Tooltip title="View Details">
                            <IconButton size="small" onClick={() => handleView(store)} sx={{ color: 'info.main' }}>
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit Store">
                            <IconButton size="small" onClick={() => handleOpenEdit(store)} sx={{ color: 'primary.main' }}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Store">
                            <IconButton size="small" onClick={() => handleDelete(store)} sx={{ color: 'error.main' }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <StoreIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body1" color="text.secondary">No stores found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredStores.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      {/* Add/Edit Store Modal */}
      <Dialog open={openAddEditModal} onClose={handleCloseAddEdit} maxWidth="md" fullWidth>
        <DialogTitle sx={{
          backgroundColor: 'primary.main',
          color: 'white',
        }}>
          <Typography variant="h6" sx={{ color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
            <StoreIcon /> {editingStore ? 'Edit Store' : 'Add New Store'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Store Code"
                value={formData.storeCode}
                onChange={(e) => setFormData({ ...formData, storeCode: e.target.value.toUpperCase() })}
                placeholder="HQ001, WH001, RT001"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Store Name"
                value={formData.storeName}
                onChange={(e) => setFormData({ ...formData, storeName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Store Type</InputLabel>
                <Select
                  value={formData.storeType}
                  onChange={(e) => setFormData({ ...formData, storeType: e.target.value })}
                  label="Store Type"
                >
                  <MenuItem value="Headquarters">Headquarters</MenuItem>
                  <MenuItem value="Warehouse">Warehouse</MenuItem>
                  <MenuItem value="Retail">Retail</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Opening Date"
                value={formData.openingDate}
                onChange={(e) => setFormData({ ...formData, openingDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                required
                label="City"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="State"
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Pincode"
                value={formData.pincode}
                onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Manager Name"
                value={formData.managerName}
                onChange={(e) => setFormData({ ...formData, managerName: e.target.value })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <ManagerIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Manager Phone"
                value={formData.managerPhone}
                onChange={(e) => setFormData({ ...formData, managerPhone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="GST Number"
                value={formData.gstNo}
                onChange={(e) => setFormData({ ...formData, gstNo: e.target.value.toUpperCase() })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Initial Stock"
                value={formData.totalStock}
                onChange={(e) => setFormData({ ...formData, totalStock: parseInt(e.target.value) || 0 })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.activeStatus}
                    onChange={(e) => setFormData({ ...formData, activeStatus: e.target.checked })}
                    color="success"
                  />
                }
                label="Active Status"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={handleCloseAddEdit}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            sx={{
              backgroundColor: 'primary.main',
              '&:hover': { backgroundColor: 'primary.dark' },
            }}
          >
            {editingStore ? 'Update Store' : 'Add Store'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Store Modal */}
      <Dialog open={openViewModal} onClose={() => setOpenViewModal(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{
          backgroundColor: 'primary.main',
          color: 'white',
        }}>
          <Typography variant="h6" sx={{ color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
            {getStoreTypeIcon(viewingStore?.storeType)} Store Details
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {viewingStore && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Store Code:</Typography>
                      <Typography variant="h6" fontWeight="bold">{viewingStore.storeCode}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Status:</Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <Chip
                          label={viewingStore.activeStatus ? "Active" : "Inactive"}
                          color={viewingStore.activeStatus ? "success" : "error"}
                          sx={{ fontWeight: 600 }}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Store Name:</Typography>
                      <Typography variant="h6" fontWeight="600">{viewingStore.storeName}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Store Type:</Typography>
                      <Chip
                        label={viewingStore.storeType}
                        sx={{
                          bgcolor: getStoreTypeColor(viewingStore.storeType).bg,
                          color: getStoreTypeColor(viewingStore.storeType).color,
                          mt: 0.5,
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Opening Date:</Typography>
                      <Typography variant="body1" fontWeight="600">{formatDate(viewingStore.openingDate)}</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>Location Details</Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body2" color="text.secondary" gutterBottom>Address:</Typography>
                <Typography variant="body1" gutterBottom>{viewingStore.address}</Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">City:</Typography>
                    <Typography variant="body1" fontWeight="600">{viewingStore.city}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">State:</Typography>
                    <Typography variant="body1" fontWeight="600">{viewingStore.state}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">Pincode:</Typography>
                    <Typography variant="body1" fontWeight="600">{viewingStore.pincode}</Typography>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>Contact Information</Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Phone:</Typography>
                    <Typography variant="body1" fontWeight="600">{viewingStore.phone}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Email:</Typography>
                    <Typography variant="body1" fontWeight="600">{viewingStore.email}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">GST Number:</Typography>
                    <Typography variant="body1" fontWeight="600">{viewingStore.gstNo}</Typography>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>Manager Details</Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Manager Name:</Typography>
                    <Typography variant="body1" fontWeight="600">{viewingStore.managerName}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Manager Phone:</Typography>
                    <Typography variant="body1" fontWeight="600">{viewingStore.managerPhone}</Typography>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Paper sx={{ p: 3, bgcolor: 'rgba(83, 109, 254, 0.05)', textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">Total Stock</Typography>
                  <Typography variant="h3" fontWeight="bold" color="primary">
                    {viewingStore.totalStock.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">units</Typography>
                </Paper>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setOpenViewModal(false)}>Close</Button>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => {
              setOpenViewModal(false);
              handleOpenEdit(viewingStore);
            }}
            sx={{
              backgroundColor: 'primary.main',
              '&:hover': { backgroundColor: 'primary.dark' },
            }}
          >
            Edit Store
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StoreMaster;

